import type { AccessRequestNodeFragment } from '@frameio/access-requests/gql/fragments/generated/AccessRequestNode';
import { useGrantedProject } from '@frameio/access-requests/hooks/useGrantedProject';
import type { DeepPartial } from '@frameio/utils';

/**
 * @description The `useFormatAccessRequestFooter` hook transforms access request node data into
 * a notification footer format. This hook extracts the access request ID and timestamp, fetches
 * the granted project information using the `useGrantedProject` hook, and formats the footer
 * data to match the NotificationLayoutItemProjectName structure.
 *
 * @param {AccessRequestNodeFragment | DeepPartial<AccessRequestNodeFragment>} accessRequestNodeData the access request node data containing ID and timestamp information.
 *
 * @returns {Object} an object containing the formatted footer information.
 * @property {string | undefined} projectName the name of the granted project.
 * @property {string} timestamp the timestamp when the access was requested.
 * @property {string} __typename the GraphQL typename for the notification layout item.
 */

export function useFormatAccessRequestFooter(
  accessRequestNodeData:
    | AccessRequestNodeFragment
    | DeepPartial<AccessRequestNodeFragment>
) {
  const { id, requestedAt } = accessRequestNodeData;

  const grantedProject = useGrantedProject({ id: id ?? '' });

  return {
    projectName: grantedProject?.name,
    timestamp: requestedAt ?? '',
    __typename: 'NotificationLayoutItemProjectName',
  };
}
